import type { OperatingSystemJob } from "@/lib/operating-systems";

type AppJobType = OperatingSystemJob["job_type"];

const SESSION_UPDATE_NOTIFICATION_JOBS_KEY = "applyt_os_session_update_notification_jobs";

function loadSessionUpdateNotificationJobs(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const value = JSON.parse(window.sessionStorage.getItem(SESSION_UPDATE_NOTIFICATION_JOBS_KEY) || "[]");
    return Array.isArray(value)
      ? value.filter((jobId): jobId is string => typeof jobId === "string")
      : [];
  } catch {
    return [];
  }
}

/** Records the session that started an App update, so only it receives the completion toast. */
export function rememberAppUpdateNotificationJob(jobId: string | null | undefined) {
  if (!jobId || typeof window === "undefined") return;
  const jobs = loadSessionUpdateNotificationJobs();
  if (jobs.includes(jobId)) return;
  window.sessionStorage.setItem(
    SESSION_UPDATE_NOTIFICATION_JOBS_KEY,
    JSON.stringify([...jobs, jobId].slice(-80)),
  );
}

export function shouldNotifyCompletedAppJob(job: Pick<OperatingSystemJob, "id" | "job_type">): boolean {
  return job.job_type !== "update" || loadSessionUpdateNotificationJobs().includes(job.id);
}

export type CompletedAppJobNotification = {
  title: string;
  actionLabel: string;
  destination: "app_details" | "templates";
};

export function completedAppJobNotification(
  jobType: AppJobType,
): CompletedAppJobNotification {
  if (jobType === "template_export") {
    return {
      title: "Template ready",
      actionLabel: "View Template",
      destination: "app_details",
    };
  }

  if (jobType === "template_retire") {
    return {
      title: "App editable again",
      actionLabel: "Open App",
      destination: "app_details",
    };
  }

  if (jobType === "update") {
    return {
      title: "App updated",
      actionLabel: "Open App",
      destination: "app_details",
    };
  }

  return {
    title: "Your App is ready",
    actionLabel: "Open App",
    destination: "app_details",
  };
}

export function completedAppJobRequiresRuntime(jobType: AppJobType): boolean {
  return jobType === "create" || jobType === "update" || jobType === "template_instance";
}
